import React, { useState, useEffect } from 'react';
import { getStorage, ref, getDownloadURL, listAll } from 'firebase/storage';
import { useLocale } from '../context/DynamicLocaleProvider';
import { getInvoiceStatusLocalizedDisplayName } from '../utils/invoiceStatuses';
import { Favicon } from '../utils/favicon';
import { getEmbeddingBadgeKind } from '../utils/embeddingBadge';
import { GlobalButton } from './GlobalButton';
import { StatusBadge } from './StatusBadge';
import styles from './DocumentTile.module.css';

export interface Document {
  id: string;
  documentId: string;
  title: string;
  sender: string;
  createdAt: Date;
  detectedDocumentCategory: string;
  status: 'submitted' | 'refunded' | 'declined' | 'approved' | 'rejected';
  taskCount?: number;
  doneTaskCount?: number;
  currency?: string;
  dueDate?: string;
  invoiceDate?: string;
  invoiceNumber?: string;
  paymentTerms?: string;
  receiver?: string;
  senderWebsiteUrl?: string;
  summary?: string;
  subtotal?: string;
  taxAmount?: string;
  totalAmount?: string;
  userId?: string;
  content?: string; // Full invoice text content for AI analysis
  fileName?: string; // Original filename of the uploaded document
}

// Project document interface (for project documents)
export interface ProjectDocumentData {
  id: string;
  documentId: string;
  filename: string;
  fileUrl?: string;
  storagePath?: string | null;
  contentType?: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  createdAt: Date;
  projectId?: string;
  userId?: string;
  // Extraction metadata
  ursExtracted?: boolean;
  ursCount?: number;
  fsExtracted?: boolean;
  fsCount?: number;
  testExtracted?: boolean;
  testCount?: number;
  riskExtracted?: boolean;
  riskCount?: number;
  sopExtracted?: boolean;
  sopCount?: number;
  embeddingEnabled?: boolean;
  embeddingSucceeded?: boolean;
  chunksCreated?: number;
  embeddingError?: string | null;
}

type DocumentTileVariant = 'tile' | 'list';

interface DocumentTileProps {
  document: Document | ProjectDocumentData;
  variant?: DocumentTileVariant;
  onClick?: (id: string) => void;
  onView?: (id: string, fileUrl?: string) => void;
  onDelete?: (id: string) => void;
  metadata?: React.ReactNode;
  statusDisplay?: React.ReactNode;
  isProjectDocument?: boolean;
}

export const DocumentTile: React.FC<DocumentTileProps> = ({ 
  document, 
  variant = 'tile',
  onClick,
  onView,
  onDelete,
  metadata,
  statusDisplay,
  isProjectDocument = false
}) => {
  const { t, locale } = useLocale();
  const [imageError, setImageError] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Type guards
  const isInvoiceDoc = (doc: Document | ProjectDocumentData): doc is Document => {
    return 'title' in doc && 'sender' in doc;
  };

  const invoiceDoc = isInvoiceDoc(document) ? document : null;
  const projectDoc = isProjectDocument ? (document as ProjectDocumentData) : null;

  const embeddingBadgeKind =
    isProjectDocument && projectDoc
      ? getEmbeddingBadgeKind(
          projectDoc.embeddingEnabled,
          projectDoc.embeddingSucceeded,
          projectDoc.chunksCreated,
          projectDoc.status
        )
      : null;

  // Load the first page image from Firebase Storage
  useEffect(() => {
    if (!document) {
      setImageUrl(null);
      return;
    }
    
    if (!document.documentId && !document.id) {
      setImageUrl(null);
      return;
    }

    let isCancelled = false;
    (async () => {
      try {
        const storage = getStorage();
        let foundImage = false;
        
        // Handle project documents - try project storage path first
        if (isProjectDocument && projectDoc) {
          if (projectDoc.storagePath) {
            try {
              const fileRef = ref(storage, projectDoc.storagePath);
              const url = await getDownloadURL(fileRef);
              if (!isCancelled) {
                setImageUrl(url);
                setImageError(false);
                foundImage = true;
              }
            } catch (error: any) {
              // Silently handle 404 errors
            }
          }
          
          // Prefer explicit fileUrl metadata as fallback (avoid noisy legacy path probing).
          if (!foundImage && projectDoc.fileUrl) {
            if (!isCancelled) {
              setImageUrl(projectDoc.fileUrl);
              setImageError(false);
              foundImage = true;
            }
          }
        }
        
        // Handle invoice documents - Files are stored directly in invoices/{userId}/filename.pdf
        if (!foundImage && invoiceDoc && invoiceDoc.userId) {
          try {
            const userInvoicesRef = ref(storage, `invoices/${invoiceDoc.userId}`);
            const listResult = await listAll(userInvoicesRef);
            
            // Try to match files by documentId, id, or fileName
            const basePaths = [];
            if (invoiceDoc.documentId) basePaths.push(invoiceDoc.documentId);
            if (invoiceDoc.id && invoiceDoc.id !== invoiceDoc.documentId) basePaths.push(invoiceDoc.id);
            
            for (const itemRef of listResult.items) {
              if (isCancelled || foundImage) break;
              
              const itemName = itemRef.name;
              let shouldTry = false;
              
              // Check if filename contains documentId or id
              if (basePaths.some(basePath => itemName.includes(basePath))) {
                shouldTry = true;
              }
              // Check if filename matches provided fileName (skip generic placeholders)
              else if (invoiceDoc.fileName && 
                       invoiceDoc.fileName.trim() &&
                       invoiceDoc.fileName.toLowerCase() !== 'scanned document' &&
                       invoiceDoc.fileName.toLowerCase() !== 'scanned document.pdf' &&
                       invoiceDoc.fileName.toLowerCase() !== 'document' &&
                       invoiceDoc.fileName.toLowerCase() !== 'document.pdf' &&
                       invoiceDoc.fileName.toLowerCase() !== 'invoice' &&
                       invoiceDoc.fileName.toLowerCase() !== 'invoice.pdf' &&
                       invoiceDoc.fileName.includes('.') && // Must have file extension
                       itemName.includes(invoiceDoc.fileName)) {
                shouldTry = true;
              }
              
              if (shouldTry) {
                try {
                  const url = await getDownloadURL(itemRef);
                  if (!isCancelled) {
                    setImageUrl(url);
                    setImageError(false);
                    foundImage = true;
                    break; // Success, exit loop
                  }
                } catch (error: any) {
                  // Silently handle 404 errors (file not found) - this is expected for missing files
                }
              }
            }
          } catch (error: any) {
            // Silently handle 404 errors (directory not found) - this is expected for users with no files
          }
        }
        
        // If still no file found and we have fileName, try direct path (for invoices)
        if (!foundImage && invoiceDoc) {
          const fileName = invoiceDoc.fileName?.trim();
          const fileNameLower = fileName?.toLowerCase() || '';
          const isGenericFileName = fileName && (
            fileNameLower === 'scanned document' ||
            fileNameLower === 'scanned document.pdf' ||
            fileNameLower.startsWith('scanned document') ||
            fileNameLower === 'document' ||
            fileNameLower === 'document.pdf' ||
            fileNameLower === 'invoice' ||
            fileNameLower === 'invoice.pdf' ||
            !fileName.includes('.') // No file extension
          );
          
          if (fileName && invoiceDoc.userId && !isCancelled && !isGenericFileName) {
            try {
              const fileRef = ref(storage, `invoices/${invoiceDoc.userId}/${fileName}`);
              const url = await getDownloadURL(fileRef);
              if (!isCancelled) {
                setImageUrl(url);
                setImageError(false);
                foundImage = true;
              }
            } catch (error: any) {
              // Silently handle 404 errors (file not found) - this is expected for missing files
            }
          }
        }
        
        if (!foundImage && !isCancelled) {
          setImageError(true);
          setImageUrl(null);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error loading document image:', error);
        }
        if (!isCancelled) {
          setImageError(true);
          setImageUrl(null);
        }
      }
    })();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.documentId, document?.id, isProjectDocument, projectDoc?.storagePath, projectDoc?.projectId, invoiceDoc?.fileName, invoiceDoc?.userId]);

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatAmount = (): string => {
    if (invoiceDoc?.totalAmount) {
      const amount = parseFloat(invoiceDoc.totalAmount);
      const currency = invoiceDoc.currency || 'EUR';
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
      }).format(amount);
    }
    return t('document_unknown_amount');
  };

  const getStatusVariant = (status: string) => {
    if (isProjectDocument) {
      switch (status) {
        case 'completed':
          return 'success';
        case 'failed':
          return 'error';
        case 'queued':
          return 'default';
        case 'processing':
          return 'warning';
        default:
          return 'default';
      }
    }
    return 'default';
  };

  const handleClick = () => {
    if (onClick) {
      onClick(document.id);
    } else if (invoiceDoc) {
      window.location.href = `/document/${document.id}`;
    }
  };

  if (!document) {
    return (
      <div className={styles.emptyState}>
        <img
          src="/assets/graphics/thescon-empty-state.svg"
          alt="No documents"
          className={styles.emptyStateImage}
          width={128}
          height={128}
        />
        <p className={styles.emptyStateText}>
          {t('document_list_no_documents_message')}
        </p>
      </div>
    );
  }

  // List variant for project documents
  if (variant === 'list' && isProjectDocument && projectDoc) {
    return (
      <div className={styles.listWrapper}>
        <div className={styles.listCard}>
          <div className={styles.listInfo}>
            <div className={styles.listHeader}>
              <h3 className={styles.listName}>{projectDoc.filename}</h3>
              <div className={styles.listHeaderBadges}>
                {statusDisplay || (
                  <StatusBadge
                    text={
                      projectDoc.status === 'completed'
                        ? t('project_tab_status_completed') || 'Completed'
                        : projectDoc.status === 'failed'
                          ? t('project_tab_status_failed') || 'Failed'
                          : projectDoc.status === 'queued'
                            ? t('project_tab_status_queued') || 'Queued'
                            : t('project_tab_status_processing') || 'Processing'
                    }
                    variant={getStatusVariant(projectDoc.status)}
                  />
                )}
                {embeddingBadgeKind !== null && (
                  <span
                    className={`${styles.embeddingBadge} ${styles[`embeddingBadge_${embeddingBadgeKind}`]}`}
                    title={
                      embeddingBadgeKind === 'fail' && projectDoc.embeddingError
                        ? String(projectDoc.embeddingError)
                        : undefined
                    }
                  >
                    {t(`document_page_embedding_badge_${embeddingBadgeKind}`)}
                  </span>
                )}
              </div>
            </div>
            <div className={styles.listMeta}>
              <span className={styles.metaItem}>
                {formatDate(projectDoc.createdAt)}
              </span>
              {metadata || (
                <>
                  {projectDoc.ursExtracted && (projectDoc.ursCount ?? 0) > 0 && (
                    <span className={styles.metaItem}>
                      {projectDoc.ursCount} {t('project_tab_urs_extracted') || 'URS extracted'}
                    </span>
                  )}
                  {projectDoc.fsExtracted && (projectDoc.fsCount ?? 0) > 0 && (
                    <span className={styles.metaItem}>
                      {projectDoc.fsCount} {t('project_tab_fs_extracted') || 'FS extracted'}
                    </span>
                  )}
                  {projectDoc.testExtracted && (projectDoc.testCount ?? 0) > 0 && (
                    <span className={styles.metaItem}>
                      {projectDoc.testCount} {t('project_tab_tests_extracted') || 'Tests extracted'}
                    </span>
                  )}
                  {projectDoc.riskExtracted && (projectDoc.riskCount ?? 0) > 0 && (
                    <span className={styles.metaItem}>
                      {projectDoc.riskCount} {t('project_tab_risks_extracted') || 'Risks extracted'}
                    </span>
                  )}
                  {projectDoc.sopExtracted && (projectDoc.sopCount ?? 0) > 0 && (
                    <span className={styles.metaItem}>
                      {projectDoc.sopCount} {t('project_tab_sops_extracted') || 'SOPs extracted'}
                    </span>
                  )}
                  {projectDoc.contentType && (
                    <span className={styles.metaItem}>
                      {projectDoc.contentType}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
          {(onView || onDelete) && (
            <div className={styles.listActions}>
              {onView && projectDoc.fileUrl && (
                <GlobalButton
                  variant="secondary"
                  onClick={() => onView(projectDoc.id, projectDoc.fileUrl)}
                  icon={<img src="/assets/icons/eye.svg" alt="View" width={12} height={12} />}
                >
                  {t('project_tab_view') || 'View'}
                </GlobalButton>
              )}
              {onDelete && (
                <GlobalButton
                  variant="secondary"
                  destructive
                  onClick={() => onDelete(projectDoc.id)}
                  icon={<img src="/assets/icons/trash.svg" alt="Delete" width={12} height={12} />}
                >
                  {t('project_tab_delete') || 'Delete'}
                </GlobalButton>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Tile variant (default, for invoices)
  return (
    <div className={styles.tileWrapper}>
      <div onClick={handleClick} className={styles.tile}>
        <div className={styles.thumbnailHeader}>
          {imageUrl && !imageError ? (
            <img
              src={imageUrl}
              alt=""
              style={{
                filter: 'grayscale(100%) blur(1px)',
              }}
              onError={() => setImageError(true)}
            />
          ) : (
            <div style={{
              width: '100%',
              height: '100%',
              backgroundColor: 'var(--background-color)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              filter: 'grayscale(100%) blur(1px)',
            }} />
          )}
        </div>

        <div className={styles.header}>
          <div className={styles.date}>
            <b>{formatDate(document.createdAt)}</b>
            {invoiceDoc && (
              <>
                <br />
                <p style={{ color: 'var(--amethyst-smoke)' }}>{formatAmount()}</p>
              </>
            )}
          </div>
        </div>

        <div className={styles.body}>
          {invoiceDoc ? (
            <>
              <h4 className={styles.title}>
                {invoiceDoc.sender && invoiceDoc.sender !== 'Unknown' ? (
                  <>
                    {invoiceDoc.senderWebsiteUrl && (
                      <Favicon 
                        websiteUrl={invoiceDoc.senderWebsiteUrl} 
                        size={20} 
                        alt={`${invoiceDoc.sender} favicon`}
                        className={styles.favicon}
                      />
                    )}
                    From {invoiceDoc.sender}
                  </>
                ) : (
                  t('document_invoice')
                )}
              </h4>
              <h4 className={styles.title2}>
                {invoiceDoc.title && invoiceDoc.title !== 'Untitled' 
                  ? invoiceDoc.title 
                  : invoiceDoc.fileName || t('document_invoice')
                }
              </h4>
            </>
          ) : projectDoc ? (
            <>
              <h4 className={styles.title}>
                {t('Document') || 'Document'}
              </h4>
              <h4 className={styles.title2} title={projectDoc.filename}>
                {projectDoc.filename}
              </h4>
              {/* Show extraction counts if available */}
              {(projectDoc.ursCount || projectDoc.fsCount || projectDoc.testCount || projectDoc.riskCount || projectDoc.sopCount) ? (
                <div className={styles.tileMetadata}>
                  {projectDoc.ursCount && projectDoc.ursCount > 0 && (
                    <span className={styles.tileMetaItem}>
                      {projectDoc.ursCount} URS
                    </span>
                  )}
                  {projectDoc.fsCount && projectDoc.fsCount > 0 && (
                    <span className={styles.tileMetaItem}>
                      {projectDoc.fsCount} FS
                    </span>
                  )}
                  {projectDoc.testCount && projectDoc.testCount > 0 && (
                    <span className={styles.tileMetaItem}>
                      {projectDoc.testCount} Tests
                    </span>
                  )}
                  {projectDoc.riskCount && projectDoc.riskCount > 0 && (
                    <span className={styles.tileMetaItem}>
                      {projectDoc.riskCount} Risks
                    </span>
                  )}
                  {projectDoc.sopCount && projectDoc.sopCount > 0 && (
                    <span className={styles.tileMetaItem}>
                      {projectDoc.sopCount} SOPs
                    </span>
                  )}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      {/* Invoice status or project deep-memory badge */}
      <div className={styles.statusContainer}>
        {isProjectDocument && projectDoc && embeddingBadgeKind !== null ? (
          <span
            className={`${styles.embeddingBadge} ${styles[`embeddingBadge_${embeddingBadgeKind}`]}`}
            title={
              embeddingBadgeKind === 'fail' && projectDoc.embeddingError
                ? String(projectDoc.embeddingError)
                : undefined
            }
          >
            {t(`document_page_embedding_badge_${embeddingBadgeKind}`)}
          </span>
        ) : !isProjectDocument ? (
          statusDisplay || (
            invoiceDoc ? (
              <div className={`${styles.statusBadge} ${styles[invoiceDoc.status || 'submitted']}`}>
                {getInvoiceStatusLocalizedDisplayName(invoiceDoc.status || 'submitted', t)}
              </div>
            ) : null
          )
        ) : null}
      </div>
    </div>
  );
};

interface DocumentListProps {
  documents: Document[];
}

const groupDocumentsByMonth = (documents: Document[], language: string) => {
  const groups: { [key: string]: Document[] } = {};

  documents.forEach(doc => {
    const month = new Intl.DateTimeFormat(language, {
      month: 'long',
      year: 'numeric'
    }).format(doc.createdAt);

    if (!groups[month]) {
      groups[month] = [];
    }
    groups[month].push(doc);
  });

  return groups;
};

const sortMonths = (months: string[]): string[] => {
  return months.sort((a, b) => {
    const dateA = new Date(a);
    const dateB = new Date(b);
    return dateB.getTime() - dateA.getTime();
  });
};

export const DocumentList: React.FC<DocumentListProps> = ({ documents }) => {
  const { locale } = useLocale();

  if (documents.length === 0) {
    return (
      <div className={styles.emptyState}>
        <img
          src="/assets/graphics/thescon-empty-state.svg"
          alt="No documents"
          className={styles.emptyStateImage}
          width={128}
          height={128}
        />
        <p className={styles.emptyStateText}>
          No documents yet.
        </p>
      </div>
    );
  }

  const groupedDocs = groupDocumentsByMonth(documents, locale);
  const sortedMonths = sortMonths(Object.keys(groupedDocs));

  return (
    <div className={styles.documentList}>
      {sortedMonths.map(month => (
        <div key={month}>
          <div className={styles.groupHeader}>{month.toLocaleUpperCase(locale)}</div>
          <div className={styles.documentGrid}>
            {groupedDocs[month].map(doc => (
              <DocumentTile
                key={doc.id}
                document={doc}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default DocumentTile;